import { getPositionInRange } from '@/utils/fee-maths';

import { formatFeePercent } from '../utils/directory-fee-display';

type DirectoryFeeDotPlotHighlight = {
  label: string;
  percentIncVat: number;
  position: number;
} | null;

type DirectoryFeeDotPlotProps = {
  percentsIncVat: readonly number[];
  minimumPercentIncVat: number;
  maximumPercentIncVat: number;
  medianPercentIncVat: number;
  medianPosition: number;
  areaName: string;
  highlight: DirectoryFeeDotPlotHighlight;
  notPublishedLabel?: string;
  ariaLabel: string;
};

const DirectoryFeeDotPlot = ({
  percentsIncVat,
  minimumPercentIncVat,
  maximumPercentIncVat,
  medianPercentIncVat,
  medianPosition,
  areaName,
  highlight,
  notPublishedLabel,
  ariaLabel,
}: DirectoryFeeDotPlotProps) => {
  const min = minimumPercentIncVat;
  const max = maximumPercentIncVat;

  const dotPositions = percentsIncVat.map((value) => ({
    key: `${value}-${min}-${max}`,
    position: getPositionInRange({ value, min, max }),
  }));

  const unpublishedPosition = 0.5;

  return (
    <div
      aria-label={ariaLabel}
      className="relative mx-2 h-[72px] sm:mx-2.5 sm:h-[92px]"
      role="img"
    >
      <div className="absolute top-[26px] right-0 left-0 h-2 rounded-full bg-linear-to-r from-accent-tint via-chip to-fee-chart-warm sm:top-[34px] sm:h-2.5" />
      {dotPositions.map(({ key, position }) => (
        <div
          className="absolute top-[27px] size-1.5 -translate-x-1/2 rounded-full bg-line-strong sm:top-[35px] sm:size-2"
          key={key}
          style={{ left: `${position * 100}%` }}
        />
      ))}
      <div
        className="absolute top-[20px] h-5 w-0.5 -translate-x-1/2 bg-ink-muted sm:top-[26px] sm:h-6"
        style={{ left: `${medianPosition * 100}%` }}
      />
      <p
        className="absolute top-[46px] m-0 -translate-x-1/2 text-[11px] text-ink-muted tabular-nums sm:top-[56px] sm:text-xs"
        style={{ left: `${medianPosition * 100}%` }}
      >
        {areaName} median {formatFeePercent(medianPercentIncVat)}
      </p>
      {highlight != null ? (
        <>
          <div
            className="absolute top-[20px] size-5 -translate-x-1/2 rounded-full border-[3px] border-ink bg-accent sm:top-[27px] sm:size-6"
            style={{ left: `${highlight.position * 100}%` }}
          />
          <p
            className="absolute top-0 m-0 -translate-x-1/2 text-[11px] font-semibold whitespace-nowrap sm:text-xs"
            style={{ left: `${highlight.position * 100}%` }}
          >
            {highlight.label}
          </p>
        </>
      ) : notPublishedLabel != null ? (
        <>
          <div
            className="absolute top-[20px] size-5 -translate-x-1/2 rounded-full border-[3px] border-dashed border-ink-muted bg-transparent sm:top-[27px] sm:size-6"
            style={{ left: `${unpublishedPosition * 100}%` }}
          />
          <p
            className="absolute top-0 m-0 -translate-x-1/2 text-[11px] font-semibold whitespace-nowrap text-ink-muted sm:text-xs"
            style={{ left: `${unpublishedPosition * 100}%` }}
          >
            {notPublishedLabel}
          </p>
        </>
      ) : null}
      <p className="absolute top-[46px] m-0 text-[11px] text-ink-muted tabular-nums sm:top-[74px] sm:text-xs">
        Cheapest {formatFeePercent(minimumPercentIncVat)}
      </p>
      <p className="absolute top-[46px] right-0 m-0 text-[11px] text-ink-muted tabular-nums sm:top-[74px] sm:text-xs">
        Dearest {formatFeePercent(maximumPercentIncVat)}
      </p>
    </div>
  );
};

export default DirectoryFeeDotPlot;
